// [LOADING 37] Notifications module import started
performance.mark('loading-37-start');
console.log('[LOADING 37] Notifications module import started...');

import { toRefs, watch } from 'vue';
// Router import
import addRoutes from '@martyrs/src/modules/core/views/router/addRoutes.js';
import { i18nManager } from '@martyrs/src/modules/core/views/classes/i18n.manager.js';
import locales from './locales/index.js';
import notificationsRoutes from './router/notifications.router.js';
import notificationsBackofficeRoutes from './router/notifications.backoffice.router.js';
// Store
import * as storeNotifications from './store/notifications.store.js';
// Global WebSocket import
import wsManager from '@martyrs/src/modules/core/views/classes/ws.manager.js';
// Capacitor Preferences
import { Preferences } from '@capacitor/preferences';
// Layouts
import NotificationsLayout from './components/layouts/NotificationsLayout.vue';
// Sections
import NotificationPreferences from './components/sections/NotificationPreferences.vue';
import NotificationsList from './components/sections/NotificationsList.vue';
// Pages
import Notifications from './components/pages/Notifications.vue';
// Components
import NotificationItem from './components/blocks/NotificationItem.vue';
import NotificationBadge from './components/elements/NotificationBadge.vue';

performance.mark('loading-37-imports-end');
const importsTime = performance.measure('loading-37-imports', 'loading-37-start', 'loading-37-imports-end');
console.log(`[LOADING 37] All imports completed in ${importsTime.duration.toFixed(2)}ms`);

/**
 * Capacitor Push Notification handler
 */
class CapacitorPushHandler {
  constructor(store) {
    this.store = store;
    this.pushNotifications = null;
    this.device = null;
    this.isInitialized = false;
  }

  /**
   * Initialize Capacitor plugins
   */
  async initialize() {
    // Skip if running in SSR
    if (typeof window === 'undefined') {
      return false;
    }

    try {
      // Dynamic imports to prevent errors in web environment
      const { Capacitor } = await import('@capacitor/core');
      const { PushNotifications } = await import('@capacitor/push-notifications');
      const { Device } = await import('@capacitor/device');

      this.capacitor = Capacitor;
      this.pushNotifications = PushNotifications;
      this.device = Device;

      // Only proceed if running on a native platform
      if (!this.capacitor.isNativePlatform()) {
        return false;
      }

      this.isInitialized = true;
      return true;
    } catch (error) {
      console.error('Error importing Capacitor plugins:', error);
      return false;
    }
  }

  /**
   * Request permission and register for push notifications
   */
  async requestPermissions() {
    // Skip if running in SSR
    if (typeof window === 'undefined') {
      return false;
    }

    if (!this.isInitialized) {
      const initialized = await this.initialize();
      if (!initialized) return false;
    }

    try {
      // Request permission
      const permissionResult = await this.pushNotifications.requestPermissions();
      if (permissionResult.receive !== 'granted') {
        console.log('Push notification permission denied');
        return false;
      }

      // Set up event listeners
      this._setupListeners();

      // Register with Apple/Google
      await this.pushNotifications.register();
      return true;
    } catch (error) {
      console.error('Error requesting push notification permissions:', error);
      return false;
    }
  }

  /**
   * Setup push notification event listeners
   */
  _setupListeners() {
    // Registration event
    this.pushNotifications.addListener('registration', this._handleRegistration.bind(this));

    // Notification received event
    this.pushNotifications.addListener('pushNotificationReceived', this._handleNotificationReceived.bind(this));

    // Notification action performed event
    this.pushNotifications.addListener('pushNotificationActionPerformed', this._handleNotificationAction.bind(this));
  }

  /**
   * Handle registration token received
   */
  async _handleRegistration(token) {
    try {
      // Get device info
      const deviceInfo = await this.device.getInfo();
      const deviceId = await this.device.getId();

      // Prepare device data
      const deviceData = {
        deviceId: deviceId.uuid,
        deviceType: deviceInfo.platform.toLowerCase(),
        deviceToken: token.value,
      };

      // Store device info for re-registration after login
      try {
        await Preferences.set({
          key: 'notifications_device_id',
          value: deviceId.uuid
        });
        await Preferences.set({
          key: 'notifications_device_token',
          value: token.value
        });
        await Preferences.set({
          key: 'notifications_device_type',
          value: deviceInfo.platform.toLowerCase()
        });
      } catch (error) {
        console.warn('Could not save device info to preferences:', error);
      }

      // For anonymous users, add anonymousId
      if (!this.store.core.state.session.userId) {
        let anonymousId = null;
        try {
          const result = await Preferences.get({ key: 'notifications_anonymous_id' });
          anonymousId = result.value;
        } catch (error) {
          console.warn('Could not get anonymous ID from preferences:', error);
        }
        
        if (!anonymousId) {
          anonymousId = 'anon_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
          try {
            await Preferences.set({
              key: 'notifications_anonymous_id',
              value: anonymousId
            });
          } catch (error) {
            console.warn('Could not save anonymous ID to preferences:', error);
          }
        }
        deviceData.anonymousId = anonymousId;
      }

      // Register device with backend
      await this.store.notifications.actions.registerDevice(deviceData);
    } catch (error) {
      console.error('Error handling push registration:', error);
    }
  }

  /**
   * Handle received notification
   */
  _handleNotificationReceived(notification) {
    // Add notification to store
    this.store.notifications.actions.addLocalNotification({
      title: notification.title,
      body: notification.body,
      data: notification.data || {},
    });
  }

  /**
   * Handle notification action (when user taps on notification)
   */
  _handleNotificationAction(actionData) {
    if (actionData.notification && actionData.notification.data) {
      this.store.notifications.actions.handleNotificationAction(actionData.notification.data);
    }
  }

  /**
   * Remove push notification listeners
   */
  removeListeners() {
    if (typeof window === 'undefined' || !this.isInitialized) {
      return;
    }

    if (this.pushNotifications) {
      this.pushNotifications.removeAllListeners();
    }
  }
}

/**
 * Notification Manager for coordinating WebSocket and Push notifications
 */

class NotificationManager {
  constructor(store, options = {}) {
    this.store = store;
    this.options = options;
    this.pushHandler = new CapacitorPushHandler(store);
    this.initialized = false;
    this.isServer = typeof window === 'undefined';
    this._visibilityHandler = null;
    this._pageshowHandler = null;
    this._onlineHandler = null;
  }

  /**
   * Re-check push subscription on foreground return (iOS kills SW and may wipe subscriptions)
   */
  async recheckPushSubscription() {
    if (this.isServer || !('serviceWorker' in navigator) || !('PushManager' in window)) return;

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        console.warn('[WebPush] No SW registration on foreground return, re-registering...');
        await navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' });
        return;
      }

      // Force SW update check
      registration.update();

      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        // iOS wiped subscription — resubscribe
        console.log('[WebPush] Subscription lost, resubscribing...');
        await this.registerWebPush(this.store);
        return;
      }

      // Check if endpoint rotated (Apple may change it)
      const savedEndpoint = localStorage.getItem('push_endpoint');
      if (savedEndpoint && subscription.endpoint !== savedEndpoint) {
        console.log('[WebPush] Endpoint rotated, syncing with server...');
        await this.registerWebPush(this.store);
      }
    } catch (err) {
      console.error('[WebPush] recheckPushSubscription error:', err);
    }
  }

  /**
   * Setup visibilitychange/pageshow/online handlers for iOS PWA foreground recovery
   */
  setupForegroundHandlers() {
    if (this.isServer) return;

    this._visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        console.log('[Notifications] App became visible, rechecking...');
        // Reconnect WebSocket if needed
        const userId = this.store.core.state.session.userId;
        if (userId && wsManager.ws?.readyState !== WebSocket.OPEN) {
          wsManager.connect(userId).catch(err => console.error('[Notifications] WS reconnect error:', err));
        }
        // Revalidate push subscription
        this.recheckPushSubscription();
      }
    };

    this._pageshowHandler = (event) => {
      if (event.persisted) {
        // Page restored from bfcache — connections are dead
        console.log('[Notifications] Page restored from bfcache, reconnecting...');
        const userId = this.store.core.state.session.userId;
        if (userId) {
          wsManager.connect(userId).catch(err => console.error('[Notifications] WS reconnect error:', err));
        }
        this.recheckPushSubscription();
      }
    };

    this._onlineHandler = () => {
      console.log('[Notifications] Network online, reconnecting...');
      const userId = this.store.core.state.session.userId;
      if (userId && wsManager.ws?.readyState !== WebSocket.OPEN) {
        wsManager.connect(userId).catch(err => console.error('[Notifications] WS reconnect error:', err));
      }
      this.recheckPushSubscription();
    };

    document.addEventListener('visibilitychange', this._visibilityHandler);
    window.addEventListener('pageshow', this._pageshowHandler);
    window.addEventListener('online', this._onlineHandler);
  }

  /**
   * Remove foreground handlers
   */
  teardownForegroundHandlers() {
    if (this._visibilityHandler) {
      document.removeEventListener('visibilitychange', this._visibilityHandler);
      this._visibilityHandler = null;
    }
    if (this._pageshowHandler) {
      window.removeEventListener('pageshow', this._pageshowHandler);
      this._pageshowHandler = null;
    }
    if (this._onlineHandler) {
      window.removeEventListener('online', this._onlineHandler);
      this._onlineHandler = null;
    }
  }

  async registerWebPush(store) {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.warn('[WebPush] PushManager not supported');
      return;
    }

    // НЕ проверяем Notification.permission — на iOS он врёт ("denied" когда реально granted)
    // pushManager.subscribe() сам выкинет NotAllowedError если реально нет разрешения

    console.log('[WebPush] Subscribing to push manager...');
    // Use getRegistration() instead of .ready — .ready hangs on iOS PWA
    let registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      console.log('[WebPush] No SW registration, registering /sw.js...');
      registration = await navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' });
      // Wait for SW to be active
      await new Promise(resolve => {
        if (registration.active) return resolve();
        const sw = registration.installing || registration.waiting;
        if (sw) sw.addEventListener('statechange', () => { if (sw.state === 'activated') resolve(); });
      });
    }
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: process.env.VAPID_PUBLIC_KEY,
    });

    console.log('[WebPush] Subscription:', JSON.stringify(subscription));

    const deviceToken = JSON.stringify(subscription);
    const deviceId = await this.generateDeviceId();

    // Store device data for re-registration after login
    try {
      await Preferences.set({ key: 'notifications_device_id', value: deviceId });
      await Preferences.set({ key: 'notifications_device_token', value: deviceToken });
      await Preferences.set({ key: 'notifications_device_type', value: 'web' });
    } catch (error) {
      console.warn('[WebPush] Could not save device info to preferences:', error);
    }

    const deviceData = {
      deviceToken,
      deviceType: 'web',
      deviceId
    };

    // For anonymous users, get or generate anonymousId
    if (!store.core.state.session.userId) {
      let anonymousId = null;
      try {
        const result = await Preferences.get({ key: 'notifications_anonymous_id' });
        anonymousId = result.value;
      } catch (error) {
        console.warn('[WebPush] Could not get anonymous ID:', error);
      }

      if (!anonymousId) {
        anonymousId = 'anon_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        try {
          await Preferences.set({ key: 'notifications_anonymous_id', value: anonymousId });
        } catch (error) {
          console.warn('[WebPush] Could not save anonymous ID:', error);
        }
      }
      deviceData.anonymousId = anonymousId;
    }

    // Register device
    console.log('[WebPush] Registering device:', deviceData);
    await store.notifications.actions.registerDevice(deviceData);
    // Save endpoint for rotation detection
    try {
      localStorage.setItem('push_endpoint', subscription.endpoint);
    } catch (e) {}
    console.log('[WebPush] Device registered successfully');
  }

  async generateDeviceId() {
    // Try to get or generate a persistent device ID
    try {
      const result = await Preferences.get({ key: 'notifications_device_id' });
      let deviceId = result.value;
      
      if (!deviceId) {
        deviceId = 'web_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        await Preferences.set({
          key: 'notifications_device_id',
          value: deviceId
        });
      }
      return deviceId;
    } catch (error) {
      console.warn('Could not access preferences for device ID:', error);
      return 'web_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    }
  }

  async unregisterWebPush() {
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) await subscription.unsubscribe();
      }
      const deviceIdResult = await Preferences.get({ key: 'notifications_device_id' });
      if (deviceIdResult.value) {
        await this.store.notifications.actions.unregisterDevice(deviceIdResult.value);
      }
      try { localStorage.removeItem('push_endpoint'); } catch (e) {}
    } catch (err) {
      console.error('[WebPush] unregisterWebPush error:', err);
    }
  }

  async getSubscriptionState() {
    if (this.isServer || !('serviceWorker' in navigator) || !('PushManager' in window)) {
      return { permission: 'unsupported', subscribed: false };
    }
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) return { permission: 'default', subscribed: false };

      // 1. Реальная подписка — главный source of truth
      //    НО на iOS может вернуть null даже если нотификации реально приходят
      const subscription = await registration.pushManager.getSubscription();

      // 2. permissionState() — надёжнее Notification.permission на iOS
      let permission = 'default';
      try {
        permission = await registration.pushManager.permissionState({ userVisibleOnly: true });
      } catch (e) {
        // 3. Fallback на Notification.permission (может врать на iOS)
        permission = 'Notification' in window ? Notification.permission : 'default';
      }

      return { permission, subscribed: !!subscription };
    } catch (err) {
      return { permission: 'default', subscribed: false };
    }
  }

  async initialize() {
    console.log('[NOTIF DEBUG] NotificationManager.initialize() called, initialized:', this.initialized, 'isServer:', this.isServer);
    if (this.initialized || this.isServer) return;

    const userId = this.store.core.state.session.userId;
    console.log('[NOTIF DEBUG] userId:', userId);

    // Connect WebSocket only for authenticated users
    if (userId) {
      try {
        console.log('[NOTIF DEBUG] Connecting to websockets, WSS_URL:', process.env.WSS_URL);
        wsManager.initialize({
          wsUrl: process.env.WSS_URL,
          maxReconnectAttempts: 10,
          reconnectDelay: 2000,
        });

        await wsManager.connect(userId);
        console.log('[NOTIF DEBUG] wsManager.connect() resolved successfully');

        wsManager.removeModuleListeners('notification');

        await wsManager.subscribeModule('notification');
        console.log('[NOTIF DEBUG] Subscribed to notification module');

        wsManager.addEventListener(
          'notification',
          data => {
            // Handle Telegram connection event
            if (data.type === 'telegram_connected') {
              this.store.notifications.state.telegramConnected = true;
              this.store.notifications.state.telegramChatId = data.data?.chatId || null;
              return;
            }
            this.store.notifications.actions.addLocalNotification(data.data);
          },
          { module: 'notification' }
        );
      } catch (error) {
        console.error('[NOTIF DEBUG] WebSocket init FAILED:', error);
      }

      // Load notifications regardless of WS status
      try {
        await this.store.notifications.actions.getNotifications(userId);
      } catch (error) {
        console.error('[NOTIF DEBUG] Failed to load notifications:', error);
      }
    } else {
      console.log('[NOTIF DEBUG] No userId, skipping WS');
    }

    // Push registration in background — don't block initialization
    // Skip auto-init for mobile apps - will be triggered manually from Walkthrough
    if (this.options.enablePush !== false && !process.env.MOBILE_APP) {
      this.pushHandler.requestPermissions().catch(() => {});
      // Ре-регистрация только если подписка уже есть (не промптить, не опираться на Notification.permission)
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        try {
          const reg = await navigator.serviceWorker.getRegistration();
          if (reg) {
            const sub = await reg.pushManager.getSubscription();
            if (sub) {
              this.registerWebPush(this.store).catch(err => console.error('[Notifications] WebPush re-registration error:', err));
            }
          }
        } catch (err) {
          console.error('[Notifications] Subscription check error:', err);
        }
      }
    }

    // Setup foreground recovery handlers (iOS PWA: reconnect WS + revalidate push on visibility)
    this.setupForegroundHandlers();

    this.initialized = true;
    console.log('[NOTIF DEBUG] NotificationManager initialized successfully');
  }

  disconnect() {
    if (this.isServer) return;

    wsManager.removeModuleListeners('notification');
    this.pushHandler.removeListeners();
    this.teardownForegroundHandlers();
    this.initialized = false;
  }
}

/**
 * Server-side utility for pre-fetching notification data
 */
const SSRUtils = {
  /**
   * Pre-fetch notifications for SSR
   * @param {Object} store - Store instance
   * @param {Object} context - SSR context
   */
  async prefetchNotifications(store, context) {
    try {
      const userId = store.core.state.session.userId;
      if (userId) {
        // Fetch notifications without WebSocket or push setup
        await store.notifications.actions.getNotifications(userId);
      }
    } catch (error) {
      console.error('Error pre-fetching notifications for SSR:', error);
    }
  },
};

/**
 * Function to initialize the notifications module
 * @param {Object} app - Vue app instance
 * @param {Object} store - Vuex/Pinia store
 * @param {Object} router - Vue Router instance
 * @param {Object} options - Configuration options
 */
function initializeNotifications(app, store, router, options = {}) {
  // i18n регистрация — иначе компоненты badge/preferences/list рендерят
  // англ. строки даже когда проект на ru.
  i18nManager.register('notifications', locales);

  // module.manager (lazy-загрузка через global.config) прокидывает произвольный
  // конфиг модуля ТОЛЬКО внутри `settings` — top-level ключи режутся whitelist'ом
  // в register(). При ручном initialize() опции приходят плоско. Поддерживаем оба пути.
  const config = { ...options, ...(options.settings || {}) };

  // User context. routeUsers развязывает страницу нотификаций с юзер-профайлом:
  // дефолт — 'User Profile Root' (/users/:_id/notifications), но проект может
  // смонтировать под любой свой роут (например settings: { routeUsers: 'Dashboard' }).
  const routeUsers = config.routeUsers || 'User Profile Root';
  addRoutes(router, {
    parentName: routeUsers,
    basePath: 'notifications',
    routes: notificationsRoutes,
  });

  // Backoffice context
  if (!options.contexts || options.contexts.includes('backoffice')) {
    const routeBackoffice = config.routeBackoffice || 'Backoffice Root';
    addRoutes(router, {
      parentName: routeBackoffice,
      basePath: 'notifications',
      routes: notificationsBackofficeRoutes,
      routeNamePrefix: 'Backoffice',
      meta: { context: 'backoffice' },
    });
  }

  store.addStore('notifications', storeNotifications);

  // Роутер приложения для handleNotificationAction — навигация по metadata.url
  storeNotifications.setRouter(router);

  // Initialize global WebSocket if needed
  if (options.wsUrl) {
    wsManager.initialize({ wsUrl: process.env.WSS_URL });
  }

  // Create notification manager
  const notificationManager = new NotificationManager(store, {
    enablePush: options.enablePush !== false,
    maxReconnectAttempts: options.maxReconnectAttempts || 5,
    reconnectDelay: options.reconnectDelay || 3000,
    pingInterval: options.pingInterval || 30000,
  });

  // Attach notification manager to store for access in components
  store.notificationManager = notificationManager;

  // Don't auto-initialize on server
  const isServer = typeof window === 'undefined';
  const autoInit = !isServer && options.autoInit !== false;

  if (autoInit) {
    // Watch for user login/logout using store.core.state.session.userId
    // immediate: true — сработает сразу после применения initialState (гидратация)
    watch(
      () => store.core.state.session.userId,
      async (userId, oldUserId) => {
        console.log('[NOTIF DEBUG] Watch triggered, userId:', userId, 'oldUserId:', oldUserId);
        if (userId && !oldUserId) {
          // Пользователь залогинен (либо при гидратации, либо при логине)
          console.log('[NOTIF DEBUG] User authenticated branch, calling reregisterDeviceAfterLogin...');
          try {
            await store.notifications.actions.reregisterDeviceAfterLogin();
            console.log('[NOTIF DEBUG] reregister done, calling disconnect...');
          } catch (err) {
            console.error('[NOTIF DEBUG] reregister failed:', err);
          }
          notificationManager.disconnect();
          console.log('[NOTIF DEBUG] disconnect done, calling initialize...');
          try {
            await notificationManager.initialize();
            console.log('[NOTIF DEBUG] initialize done');
          } catch (err) {
            console.error('[NOTIF DEBUG] initialize FAILED:', err);
          }
        } else if (!userId && oldUserId) {
          // Пользователь разлогинился
          console.log('[Notifications] User logged out, resetting notifications...');
          notificationManager.disconnect();
          store.notifications.mutations.resetNotifications();
        } else if (!userId && !oldUserId) {
          // Анонимный пользователь — инициализируем без WebSocket
          console.log('[Notifications] Anonymous user, initializing without WebSocket...');
          await notificationManager.initialize();
        }
      },
      { immediate: true }
    );
  }

  // Provide composable for components to access notification functionality
  app.provide('useNotifications', () => {
    return {
      ...toRefs(store.notifications.state),
      ...store.notifications.actions,
      ...store.notifications.mutations,
      init: notificationManager.initialize.bind(notificationManager),
      disconnect: notificationManager.disconnect.bind(notificationManager),
      registerWebPush: () => notificationManager.registerWebPush(store),
      unregisterWebPush: () => notificationManager.unregisterWebPush(),
      getSubscriptionState: () => notificationManager.getSubscriptionState(),
      isServer,
    };
  });

  return notificationManager;
}

// Module export
const ModuleNotifications = {
  initialize: initializeNotifications,
  SSR: SSRUtils, // Export SSR utilities
  views: {
    store: {
      storeNotifications,
    },
    router: {
      notificationsRoutes,
      notificationsBackofficeRoutes,
    },
    components: {
      // Elements
      NotificationBadge,
      // Blocks
      NotificationItem,
      // Sections
      NotificationsList,
      NotificationPreferences,
      // Pages
      Notifications,
      // Layouts
      NotificationsLayout,
    },
  },
};

// Component exports
export {
  // Elements
  NotificationBadge,
  // Blocks
  NotificationItem,
  NotificationPreferences,
  // Pages
  Notifications,
  // Layouts
  NotificationsLayout,
  // Sections
  NotificationsList,
  // SSR Utilities
  SSRUtils,
};

// [LOADING 37] Notifications module import completed
performance.mark('loading-37-end');
performance.measure('loading-37-total', 'loading-37-start', 'loading-37-end');
const totalTime = performance.getEntriesByName('loading-37-total')[0];
console.log(`[LOADING 37] Notifications module fully imported in ${totalTime?.duration?.toFixed(2)}ms`);

export default ModuleNotifications;
